import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import {
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  getMultiFactorResolver,
  multiFactor,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  signInWithEmailAndPassword,
  type MultiFactorError,
  type MultiFactorResolver,
  type PhoneMultiFactorInfo,
  type User as FirebaseUser,
} from "firebase/auth";
import { auth } from "@/services/firebase";
import { call, CALLABLES } from "@/services/callables";
import { AppError } from "@/services/errors";
import type { MeResponse, User } from "@/types";

/**
 * Who is signed in, and whether they may be here at all.
 *
 * Two independent checks, on purpose:
 *
 *  1. The **custom claim** says `spontar_admin`. It is a cache, refreshed when
 *     a token is, and it is what the security rules read.
 *  2. The **user document** says the same. It is the authority, and it is what
 *     the callables read.
 *
 * Both are required. A claim that has not caught up with a revoked role would
 * otherwise leave somebody with a console they should no longer have, for as
 * long as their token lives.
 *
 * And a **second factor is required before anything else.** This console can
 * read every institution's data and change what any of them is entitled to; a
 * password on its own does not hold that.
 */

export type AdminStatus =
  | "loading"
  | "signedOut"
  | "notAdmin"
  | "needsSecondFactor"
  | "ready";

/**
 * A sign-in stopped halfway for a second factor.
 *
 * Present only between the password being accepted and the code being
 * verified. `hint` is the masked destination, because somebody with two phones
 * needs to know which one to pick up.
 */
export interface SignInChallenge {
  hint: string;
  /** True once a code has been sent and the field should be shown. */
  sent: boolean;
}

interface AuthValue {
  status: AdminStatus;
  user: User | undefined;
  firebaseUser: FirebaseUser | undefined;
  challenge: SignInChallenge | undefined;
  signIn: (email: string, password: string) => Promise<void>;
  /** Sends the SMS. The container hosts the invisible reCAPTCHA Firebase requires. */
  sendChallengeCode: (container: HTMLElement) => Promise<void>;
  completeChallenge: (code: string) => Promise<void>;
  cancelChallenge: () => void;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * The destination of the code, as much of it as is safe to show.
 *
 * Firebase already hands back a masked number; this covers the case where it
 * does not, and gives the screen one shape to render either way. Never the
 * whole number: this page is reachable by anybody who has a staff password, and
 * printing the phone it protects would hand over half the second factor.
 */
export function maskedHint(phoneNumber: string | null | undefined): string {
  const text = (phoneNumber ?? "").trim();
  if (text.length === 0) return "";
  if (text.includes("*")) return text;
  const tail = text.slice(-4);
  return `••• ${tail}`;
}

const AuthContext = createContext<AuthValue | undefined>(undefined);

/** Whether Firebase says this account has enrolled a second factor. */
export function hasSecondFactor(user: FirebaseUser): boolean {
  return multiFactor(user).enrolledFactors.length > 0;
}

/**
 * Decides what an authenticated person may see.
 *
 * Pure, so the rule can be read and tested without a browser: both signals must
 * agree, and the factor gate comes after the role gate — somebody who is not an
 * admin should be told that, rather than asked to enrol a factor for a console
 * they will then be refused.
 */
export function statusFor(
  claimIsAdmin: boolean,
  documentRole: string | undefined,
  secondFactorEnrolled: boolean
): AdminStatus {
  if (!claimIsAdmin || documentRole !== "spontar_admin") return "notAdmin";
  return secondFactorEnrolled ? "ready" : "needsSecondFactor";
}

export function AuthProvider({
  children,
  initialValue,
}: {
  children: ReactNode;
  /** Tests inject a resolved state and skip Firebase entirely. */
  initialValue?: Partial<AuthValue>;
}) {
  const [status, setStatus] = useState<AdminStatus>(initialValue?.status ?? "loading");
  const [user, setUser] = useState<User | undefined>(initialValue?.user);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | undefined>(undefined);
  const [challenge, setChallenge] = useState<SignInChallenge | undefined>(undefined);

  /*
    The resolver and the verification id are held in refs rather than in state.

    Neither is rendered, both are single-use, and putting a Firebase object in
    a dependency array is how an effect ends up re-running on every keystroke.
  */
  const resolver = useRef<MultiFactorResolver | undefined>(undefined);
  const verificationId = useRef<string | undefined>(undefined);

  const injected = initialValue !== undefined;

  const evaluate = useCallback(async (next: FirebaseUser) => {
    /*
      Ask Firebase for the profile again before deciding anything.

      `enrolledFactors` is read off the profile cached on the user object, and
      enrolling a second factor rotates the token — which fires this listener
      again with a user whose cached profile predates the enrolment. Without the
      reload, that late callback overwrites the decision made a moment earlier
      and puts somebody who has just enrolled back on the page asking them to.

      A failure here is not a reason to lock anybody out: the cached profile is
      what the previous version used for everything.
    */
    try {
      await next.reload();
    } catch {
      // Offline, or a token mid-rotation. Fall through on what we have.
    }

    const token = await next.getIdTokenResult();
    const claimIsAdmin = token.claims.role === "spontar_admin";

    let documentRole: string | undefined;
    let profile: User | undefined;
    try {
      const response = await call<undefined, MeResponse>(CALLABLES.me);
      profile = response.user;
      documentRole = response.user.role;
    } catch {
      // A token Firebase accepts and the backend refuses. Treated as not an
      // admin rather than as an error: the outcome is the same and the message
      // is more useful.
      documentRole = undefined;
    }

    setUser(profile);
    setStatus(statusFor(claimIsAdmin, documentRole, hasSecondFactor(next)));
  }, []);

  useEffect(() => {
    if (injected) return;

    return onAuthStateChanged(auth(), async (next) => {
      setFirebaseUser(next ?? undefined);
      if (!next) {
        setUser(undefined);
        setStatus("signedOut");
        return;
      }
      await evaluate(next);
    });
  }, [injected, evaluate]);

  const value = useMemo<AuthValue>(() => {
    const base: AuthValue = {
      status,
      user,
      firebaseUser,
      challenge,
      signIn: async (email, password) => {
        try {
          await signInWithEmailAndPassword(auth(), email.trim().toLowerCase(), password);
        } catch (error) {
          /*
            The password was right and the account has a second factor.

            This branch is not an edge case: this console REQUIRES a second
            factor, so every admin who has done as they were told arrives here
            on every sign-in. Without it, enrolling a factor locks the account
            out permanently — the console demands a thing and then refuses
            everybody who complies.
          */
          if ((error as { code?: string }).code === "auth/multi-factor-auth-required") {
            resolver.current = getMultiFactorResolver(auth(), error as MultiFactorError);
            const hint = resolver.current.hints[0] as PhoneMultiFactorInfo | undefined;
            setChallenge({ hint: maskedHint(hint?.phoneNumber), sent: false });
            return;
          }

          // One message for every other reason. Telling a prober which staff
          // addresses exist is not a trade worth making for a console like
          // this one.
          throw new AppError("error.auth.invalidCredentials", "unauthenticated");
        }
      },

      sendChallengeCode: async (container) => {
        const pending = resolver.current;
        if (!pending) return;

        const hint = pending.hints[0];
        if (!hint) throw new AppError("error.auth.invalidCredentials", "unauthenticated");

        // Firebase will not send an SMS without a reCAPTCHA. Invisible, so the
        // only thing anybody sees is the button they pressed.
        const verifier = new RecaptchaVerifier(auth(), container, { size: "invisible" });
        verificationId.current = await new PhoneAuthProvider(auth()).verifyPhoneNumber(
          { multiFactorHint: hint, session: pending.session },
          verifier
        );
        setChallenge((current) => (current ? { ...current, sent: true } : current));
      },

      completeChallenge: async (code) => {
        const pending = resolver.current;
        if (!pending || !verificationId.current) return;

        try {
          await pending.resolveSignIn(
            PhoneMultiFactorGenerator.assertion(
              PhoneAuthProvider.credential(verificationId.current, code.trim())
            )
          );
        } catch {
          // Distinct from the sign-in message on purpose. There is nothing to
          // enumerate here — whoever is reading this already got the password
          // right — and "wrong code" is what somebody who mistyped needs.
          throw new AppError("error.auth.invalidCode", "unauthenticated");
        }

        resolver.current = undefined;
        verificationId.current = undefined;
        setChallenge(undefined);
      },

      cancelChallenge: () => {
        resolver.current = undefined;
        verificationId.current = undefined;
        setChallenge(undefined);
      },
      signOut: async () => {
        await firebaseSignOut(auth());
      },
      refresh: async () => {
        const current = auth().currentUser;
        if (!current) return;

        /*
          Ask Firebase again, rather than re-reading what it said last time.

          Both signals this function checks are cached on the user object: the
          enrolled factors come from the profile fetched at sign-in, and the
          role claim comes from the token minted then. Enrolling a second
          factor changes the first and nothing else, so a refresh that skipped
          the reload left somebody who had just enrolled staring at the page
          asking them to enrol.
        */
        // A new token as well as a fresh profile: `evaluate` reloads the
        // profile itself, and this is the one place a stale ROLE claim — a
        // promotion or a revocation — should also be picked up.
        await current.getIdToken(true);
        await evaluate(current);
      },
    };
    return injected ? { ...base, ...initialValue } : base;
  }, [status, user, firebaseUser, challenge, evaluate, injected, initialValue]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
